import { localeStore } from 'src/mobx/localesStore';
import { IResponseProductModifier, IResponseProductModifierOption } from 'src/types/products';
import { Calories, IProduct, PRODUCT_TYPE } from '../types/products';
import { formatUnit, getTranslate } from './formatter';

/**
 * calculate multiple calories values to one
 *
 * @param {...Calories[]} calories
 * @return {(null | number)}
 */
const calculateCaloriesSum = (...calories: Calories[]): null | number =>
  calories.reduce((sum, calorie): null | number => {
    if (calorie !== null) {
      if (sum === null) sum = 0;
      sum = sum + calorie;
    }
    return sum;
  }, null);
// /**
//  * return product calories or 0
//  *
//  * @param {Calories} calories
//  * @return {*}  {number}
//  */
// const formatCalories = (calories: Calories): number => (typeof calories === 'number' ? calories : 0);

/**
 * return product.calories
 *
 * @param {(IProduct | undefined)} product
 * @return {number}  {number}
 */
const getProductMainCalories = (product: IProduct | undefined): number | null => {
  if (product?.size && typeof product?.size?.calories === 'number') {
    return product.size.calories;
  }
  if (typeof product?.calories === 'number') {
    return product.calories;
  }
  return null;
};
/**
 * method will calculate product calories based on selected variabilities
 * support all product types (like meal, deal, default)
 *
 * @param {(IProduct | undefined)} product
 * @return {*}
 */
const calculateProductCalories = (product: IProduct | undefined) => {
  if (!product) return null;

  let calories: null | number = null;

  const mainCalories = getProductMainCalories(product);

  let additionalCalories = null;

  if (product.type === undefined || product.type === PRODUCT_TYPE.PRODUCT) {
    additionalCalories = product?.modifiers.reduce(
      (productCalories: number | null, modifier: IResponseProductModifier): number | null => {
        return modifier.options.reduce((optionCalories: number | null, option: IResponseProductModifierOption) => {
          if (option.selected && typeof option.calories === 'number') {
            if (typeof option.calories === 'number') {
              optionCalories = calculateCaloriesSum(optionCalories, option.calories * option.quantity);
            }
          }
          return optionCalories;
        }, productCalories);
      },
      null
    );
  } else if (product.items && product.type === PRODUCT_TYPE.MEAL_COMBO) {
    additionalCalories = product.items?.reduce((comboCalories: null | number, item) => {
      if (product.size && item.size_id === product.size?.id && item.is_selected) {
        if (typeof item.calories === 'number') {
          comboCalories = calculateCaloriesSum(comboCalories, item.calories);
        }
      }

      return comboCalories;
    }, null);
  } else if (product.items && PRODUCT_TYPE.DEAL_COMBO === product.type) {
    additionalCalories = calculateCaloriesSum(...product.items.map((item) => item.calories));
  }

  calories = calculateCaloriesSum(mainCalories, additionalCalories);

  if (product.quantity && typeof calories === 'number') {
    calories = calories * product.quantity;
  }

  return calories;
};

/**
 * get calculated calories string if calories unit is exist in product
 *
 * @param {(IProductV4 | IProduct)} product
 * @return {*}  {(null | string)}
 */
export const getProductCalculatedCalories = (product: IProduct | undefined): null | string => {
  if (!product) return null;
  const calories = calculateProductCalories(product);
  if (typeof calories === 'number') {
    return `${calories} ${localeStore.t('kcal')}`;
  }
  return null;
};

/**
 * get calories string if calories unit is exist in product
 *
 * @param {(IProductV4 | IProduct)} product
 * @return {*}  {(null | string)}
 */
export const getProductCalories = (product: IProduct | undefined): null | string => {
  if (!product || typeof product.calories !== 'number') return null;

  if (typeof product.calories === 'number') {
    return `${product.calories} ${localeStore.t('kcal')}`;
  }
  return null;
};

/**
 * get unit string if additional unit is exist in product
 *
 * @param {IProduct} product
 * @return {*}  {(null | string)}
 */
export const getProductUnit = (product: IProduct | undefined): null | string => {
  if (!product) return null;
  const ref: IProduct | IProduct['size'] = product.size || product;
  return formatUnit(ref.unit || getTranslate(ref.unit_name), ref.weight);
};

/**
 * get list of units
 *
 * @param {IProduct} product
 * @return {*}
 */
export const getProductUnits = (product: /* IProduct | undefined */ any): (string | null)[] => {
  if (!product) return [];
  return [getProductCalories(product), getProductUnit(product)].filter(Boolean);
};

/**
 * get list of calculated units
 *
 * @param {IProduct} product
 * @return {*}
 */
export const getProductCalculatedUnits = (product: IProduct | undefined | null): (string | null)[] => {
  if (!product) return [];
  return [getProductCalculatedCalories(product), getProductUnit(product)].filter(Boolean);
};

/**
 * get calories string from option
 *
 * @param {IResponseProductModifierOption} option
 * @return {*}  {(null | string)}
 */
export const getOptionCalories = (option: IResponseProductModifierOption | undefined): null | string => {
  if (!option || typeof option.calories !== 'number') return null;

  return `${option.calories} ${localeStore.t('kcal')}`;
};

/**
 * get unit string if additional unit is exist in option
 *
 * @param {IResponseProductModifierOption} option
 * @return {*}  {(null | string)}
 */
export const getOptionUnit = (option: IResponseProductModifierOption | undefined): null | string => {
  if (!option || !option.unit) return null;
  return formatUnit(option.unit, option.weight);
};

/**
 * get list of units
 *
 * @param {IResponseProductModifierOption} product
 * @return {*}
 */
export const getOptionUnits = (option: IResponseProductModifierOption | undefined): (string | null)[] => {
  return [getOptionCalories(option), getOptionUnit(option)].filter(Boolean);
};
